/**
 * The signed-in gardener's preferences, as a React context: the shape, the
 * defaults, and useUserConfig to read and save them (UserConfigProvider
 * fills it).
 *
 * Who uses it: UserConfigProvider, ThemeProvider, the profile, Today,
 * Weather, the plant card's readings and the hooks that read the unit
 * (usePlantOutlook, useWeatherNow, useTemperatureClassification), the
 * season review and useSeason (the choice of season), and api/preferences
 * for the Preferences type.
 *
 * @module userConfigStore
 */

import { createContext, useContext } from 'react';
import { Language } from '@/shared/types/i18n';
import { UserConfig, Hemisphere } from '@/config/config';

/** A gardener's preferences, kept on their account (routes/user-settings). */
export interface Preferences {
  /** The theme (ThemeContext's Theme); a retired name is read as Grow. */
  theme: 'grow' | 'light' | 'dark' | 'fieldnotes';
  /** The app's language, 'en' or 'es'. */
  language: Language;
  /** The root font size's step (the Grow settings ui.ROOT_FONT_*_PX). */
  fontSize: 'small' | 'medium' | 'large';
  /** The garden's hemisphere, northern or southern. */
  hemisphere: Hemisphere;
  /** The unit temperatures are shown in. */
  temperatureUnit: 'C' | 'F';
  /** How many days ahead Today's Coming up looks, 0 to 7; null for the Grow settings' default. */
  headsUpDays: number | null;
  /** "Move my plants' stages for me": Grow's automatic stages, on unless switched off. */
  autoStages: boolean;
  /** What counts as the season for the season review (audit A72); absent until the account is read, when the server's choice holds. */
  seasonReviewBy?: 'growing' | 'calendar';
}

/** What the context gives: the config, and ways to change, save and reload it. */
interface UserConfigContextType {
  /** The config, its preferences as saved (or the defaults before they arrive). */
  userConfig: UserConfig;
  /** Applies a config at once; saves it only when the theme changed. */
  updateUserConfig: (newConfig: UserConfig) => void;
  /** Saves preferences, whole; throws when the save fails. */
  savePreferences: (preferences: Preferences) => Promise<void>;
  /** Reads the preferences again from the account. */
  refreshPreferences: () => Promise<void>;
}

/*
 * The defaults live in config.ts as DEFAULT_USER_CONFIG. Copies of them here
 * (defaultPreferences, defaultUserConfig) were imported by nothing and could
 * only drift from the real ones, so they went.
 */

/** The context itself; UserConfigProvider fills it. */
export const UserConfigContext = createContext<UserConfigContextType | undefined>(undefined);

/**
 * The gardener's config and the ways to change it.
 *
 * @returns The provider's value.
 * @throws When used outside UserConfigProvider.
 */
export function useUserConfig(): UserConfigContextType {
  const context = useContext(UserConfigContext);
  if (context === undefined) {
    throw new Error('useUserConfig must be used within a UserConfigProvider');
  }
  return context;
} 